import React, { useState, useEffect, useMemo } from "react";
import { GridFilterCellProps } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { emitTagChange } from "../helper/tagFilterCallbackStore";

interface AccountItem {
  tags?: string[];
  [key: string]: any;
}

// Extend the GridFilterCellProps type to include the properties we need
interface ExtendedGridFilterCellProps extends GridFilterCellProps {
  data?: AccountItem[];
  filterDescriptor?: {
    gridData?: AccountItem[];
    [key: string]: any;
  };
}

const TagFilterCell: React.FC<ExtendedGridFilterCellProps> = (props) => {
  const { onChange, data } = props;
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Load initial filter state if it exists
  useEffect(() => {
    if (props.value && typeof props.value === "string") {
      const tags = [props.value];
      setSelectedTags(tags);
    } else if (props.value && Array.isArray(props.value)) {
      setSelectedTags(props.value);
    }
  }, [props.value]);

  // Extract all unique tags from all accounts
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();

    // Access data from props - this is the entire dataset from the Grid component
    if (data && Array.isArray(data)) {
      data.forEach((item) => {
        if (item.tags && Array.isArray(item.tags)) {
          item.tags.forEach((tag: string) => tagSet.add(tag));
        }
      });
    }

    // If we have gridData from the filter context
    else if (
      props.filterDescriptor?.gridData &&
      Array.isArray(props.filterDescriptor.gridData)
    ) {
      const gridData = props.filterDescriptor.gridData as AccountItem[];
      gridData.forEach((item) => {
        if (item.tags && Array.isArray(item.tags)) {
          item.tags.forEach((tag: string) => tagSet.add(tag));
        }
      });
    }

    return Array.from(tagSet).sort();
  }, [data, props.filterDescriptor?.gridData]);

  const handleTagChange = (tag: string) => {
    let newSelectedTags;
    if (selectedTags.includes(tag)) {
      newSelectedTags = selectedTags.filter((t) => t !== tag);
    } else {
      newSelectedTags = [...selectedTags, tag];
    }
    setSelectedTags(newSelectedTags);

    // Apply the filter immediately
    if (newSelectedTags.length > 0) {
      // Emit the tag change event
      emitTagChange({ selectedTags: newSelectedTags });

      // If only one tag is selected, pass it as a string
      // otherwise pass the array for our custom filter handling
      const tagValue =
        newSelectedTags.length === 1 ? newSelectedTags[0] : newSelectedTags;

      // Apply the filter through the onChange callback
      onChange?.({
        value: tagValue,
        operator: "contains",
        syntheticEvent: {
          nativeEvent: new Event("change"),
        } as React.SyntheticEvent<any, Event>,
      });
    } else {
      // Clear the filter if no tags are selected
      emitTagChange({ selectedTags: [] });

      onChange?.({
        value: null,
        operator: "",
        syntheticEvent: {
          nativeEvent: new Event("change"),
        } as React.SyntheticEvent<any, Event>,
      });
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "8px",
        maxHeight: "300px",
      }}
    >
      <div style={{ overflowY: "auto", maxHeight: "250px" }}>
        {allTags.map((tag) => (
          <div key={tag} style={{ marginBottom: "4px" }}>
            <Checkbox
              checked={selectedTags.includes(tag)}
              onChange={() => handleTagChange(tag)}
              label={tag}
            />
          </div>
        ))}
        {allTags.length === 0 && (
          <div style={{ textAlign: "center", color: "#666", padding: "10px" }}>
            No tags found
          </div>
        )}
      </div>
    </div>
  );
};

export default TagFilterCell;
